// Suppress NavigatorLockAcquireTimeoutError early - before any imports
// This error is non-critical and occurs when multiple tabs try to acquire the same Supabase auth lock
if (typeof window !== 'undefined') {
  const isLockError = (error: any): boolean => {
    if (!error) return false;
    
    const errorName = error?.name || '';
    const errorMessage = String(error?.message || '');
    const errorString = String(error || '');
    const errorStack = String(error?.stack || '');
    
    return (
      errorName === 'NavigatorLockAcquireTimeoutError' ||
      errorMessage.includes('NavigatorLockAcquireTimeoutError') ||
      errorMessage.includes('lock:sb-') ||
      errorString.includes('NavigatorLockAcquireTimeoutError') ||
      errorString.includes('lock:sb-') ||
      errorStack.includes('NavigatorLockAcquireTimeoutError') ||
      errorStack.includes('locks.js')
    );
  };

  const suppressErrorEvent = (event: ErrorEvent): void => {
    if (isLockError(event.error)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  const suppressRejectionEvent = (event: PromiseRejectionEvent): void => {
    if (isLockError(event.reason)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Use capture phase to catch errors early, before they bubble
  window.addEventListener('error', suppressErrorEvent, true);
  window.addEventListener('unhandledrejection', suppressRejectionEvent, true);
  
  // Also catch console errors (some browsers log these separately)
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    const errorString = args.map(arg => String(arg)).join(' ');
    if (!errorString.includes('NavigatorLockAcquireTimeoutError') && 
        !errorString.includes('lock:sb-')) {
      originalConsoleError.apply(console, args);
    }
  };
}

import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient } from '@angular/common/http';
import {
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
  PreloadAllModules,
} from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import {
  importProvidersFrom,
  ErrorHandler,
  Injectable,
  provideZoneChangeDetection,
} from '@angular/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { initFirebaseWeb } from './firebase-web-init';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

initFirebaseWeb(environment);

if (environment.sentry?.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.production
      ? 'production'
      : environment.envBadge === 'STAGING'
        ? 'staging'
        : 'development',
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
  });
}

@Injectable()
class CleannoGlobalErrorHandler implements ErrorHandler {
  private readonly sentryHandler =
    environment.sentry?.dsn != null ? Sentry.createErrorHandler({ showDialog: false }) : null;

  handleError(error: unknown): void {
    const msg = error instanceof Error ? error.message : String(error);

    if (msg.includes('NavigatorLockAcquireTimeoutError') || msg.includes('lock:sb-')) {
      return;
    }

    if (this.sentryHandler) {
      this.sentryHandler.handleError(error);
    } else {
      console.error('[GlobalErrorHandler]', error);
    }
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: ErrorHandler, useClass: CleannoGlobalErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({ mode: 'md' }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'nl',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
  ],
});
